/*
 * Navigation facts shared by the header, the mobile drawer and the footer:
 * which platforms get a quick link, which price buckets exist, and what the
 * pre-order deeplink looks like. Every entry is a plain `/catalog?…` URL that
 * CatalogPage seeds its filters from — nothing here knows filter state.
 */
import type { RouteLocationRaw } from 'vue-router';
import type { PLATFORM_ICON_PATHS } from '../../data/platformIcons';
import type { Facet } from '../../types/product';

export interface PlatformLink {
  name: string;
  /**
   * Tested against the lower-cased facet name. `Product.platform` holds raw
   * Kinguin feed values ("PlayStation 5", "Xbox Series X|S", "GOG.com"), so a
   * link claims every live value it matches instead of naming one.
   */
  match: RegExp;
  icon: keyof typeof PLATFORM_ICON_PATHS;
}

export const PLATFORM_LINKS: PlatformLink[] = [
  { name: 'Steam', match: /steam/, icon: 'steam' },
  { name: 'PlayStation', match: /playstation|\bps\d?\b|psn/, icon: 'playstation' },
  { name: 'Xbox', match: /xbox/, icon: 'xbox' },
  { name: 'Nintendo', match: /nintendo|switch/, icon: 'nintendo' },
  { name: 'Epic Games', match: /epic/, icon: 'epic' },
  { name: 'GOG', match: /gog/, icon: 'gog' },
];

/** The live facets behind a link; empty until the facets arrive. */
export function platformFacets(p: PlatformLink, facets: Facet[] | null): Facet[] {
  return (facets ?? []).filter((f) => f.count > 0 && p.match.test(f.name.toLowerCase()));
}

/** Products behind a link; null until the facets arrive. */
export function platformCount(p: PlatformLink, facets: Facet[] | null): number | null {
  return facets ? platformFacets(p, facets).reduce((n, f) => n + f.count, 0) : null;
}

export const PREORDER_LINK: RouteLocationRaw = { path: '/catalog', query: { preorder: '1' } };

export interface PriceBucket {
  /** Suffix of the `header.price.*` i18n key. */
  key: 'under5' | 'from5' | 'from10' | 'from20';
  /** Euros, the unit CatalogPage's `filters.priceMin/priceMax` use. */
  query: { priceMin?: string; priceMax?: string };
}

export const PRICE_BUCKETS: PriceBucket[] = [
  { key: 'under5', query: { priceMax: '5' } },
  { key: 'from5', query: { priceMin: '5', priceMax: '10' } },
  { key: 'from10', query: { priceMin: '10', priceMax: '20' } },
  { key: 'from20', query: { priceMin: '20' } },
];

export function priceBucketLink(bucket: PriceBucket): RouteLocationRaw {
  return { path: '/catalog', query: bucket.query };
}

/**
 * Comma-joined feed values, so the catalog lands with the whole platform
 * selected. Before the facets arrive this is plain `/catalog`, never a filter
 * that matches nothing.
 */
export function platformLink(p: PlatformLink, facets: Facet[] | null): RouteLocationRaw {
  const names = platformFacets(p, facets).map((f) => f.name);
  return names.length ? { path: '/catalog', query: { platforms: names.join(',') } } : { path: '/catalog' };
}
